/**
 * Bottom toast shown when a newer build has been deployed than the one this tab
 * is running (see useVersionCheck). Non-blocking: "Refresh" reloads onto the new
 * version, "Later" dismisses for this tab. Rendered once globally in App.tsx.
 *
 * Palette: monochrome + green only (green CTA), semantic theme tokens — works in
 * light + dark.
 */
import React, { useState } from 'react';
import { Snackbar, Box, Typography, Button } from '@mui/material';
import { baseBrandColors, typography } from '../common/styles/universalTheme';
import { useVersionCheck } from '../hooks/useVersionCheck';

const UpdateAvailableToast: React.FC = () => {
  const { updateAvailable } = useVersionCheck();
  const [dismissed, setDismissed] = useState(false);

  return (
    <Snackbar
      open={updateAvailable && !dismissed}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      // Sit above the mobile bottom nav / floating chat launcher.
      sx={{ mb: { xs: 9, sm: 3 }, zIndex: (t) => t.zIndex.snackbar }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          maxWidth: 440,
          px: 2,
          py: 1.25,
          bgcolor: 'background.paper',
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          boxShadow: 4,
        }}
      >
        <Typography sx={{ fontSize: typography.fontSize.base, color: 'text.primary' }}>
          A new version is ready.
        </Typography>
        <Button
          size="small"
          onClick={() => window.location.reload()}
          sx={{
            ml: 'auto',
            bgcolor: baseBrandColors.buttons.green.main,
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: typography.fontWeight.semibold,
            whiteSpace: 'nowrap',
            '&:hover': { bgcolor: baseBrandColors.buttons.green.hover },
          }}
        >
          Refresh
        </Button>
        <Button
          size="small"
          onClick={() => setDismissed(true)}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            minWidth: 'auto',
            '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
          }}
        >
          Later
        </Button>
      </Box>
    </Snackbar>
  );
};

export default UpdateAvailableToast;
